// 👇 Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// 👇 Axios Imports
import axios from 'axios'

import { urlFreightForwardService, urlIdentityService, urlOceanBookingService, urlDriverService, urlTerminalService, urlEquipmentService, urlEquipmentScheduleService, urlMovementService, urlEdiRecords, urlConfigCacheService, urlTurnaroundService, urlDemurrageService } from '@src/endpoints'

import { getUriParams } from '@src/utility/Utils'
import toast from 'react-hot-toast'
import { ToastContent } from "@src/views/components/toast/ToastContent"

const ToastMessage = (heading, message, isSuccess) => {
  toast(t => (
    <ToastContent t={t} heading={heading} response={isSuccess} message={message} />
  ))
}


export const getAllData = createAsyncThunk('ffRequests/getAllData', async () => {
  const response = await axios.get(`${urlFreightForwardService}/GetFFStatusCount`)
  return response.data
})

export const getScheduleEquipmentStats = createAsyncThunk('equipment/getScheduleEquipmentStats', async requestBody => {
  const statsResponse = await axios.post(`${urlEquipmentScheduleService}/GetScheduleEquipmentStats`, requestBody)
  return { statsData: statsResponse.data.data }
})

export const getData = createAsyncThunk('equipment/getData', async (data, { dispatch }) => {
  const { params, filter, sortFields } = data
  
  const isSchedule = filter.movementType === 'schedule'
  const baseService = isSchedule ? urlEquipmentScheduleService : urlEquipmentService
  
  // Use sortFields if available and not empty, otherwise fall back to sortColumn
  const hasSortFields = sortFields && sortFields.length > 0
  
  let uri, requestBody, endpoint
  
  if (hasSortFields) {
    // Use multi-sort endpoint with sortFields in body
    const paramsWithoutSort = { ...params }
    delete paramsWithoutSort.sortColumn
    delete paramsWithoutSort.sort
    uri = getUriParams(paramsWithoutSort)
    
    if (isSchedule) {
      endpoint = `${baseService}/GetScheduleEquipmentList?${uri}`
    } else {
      endpoint = `${baseService}/GetEquipmentByMovementTypeMultiSort?${uri}`
    }
    
    const filterData = {
      movementType: filter.movementType || "",
      yardLocation: filter.yardLocation || "ALL",
      customerName: filter.customerName === "ALL" || filter.customerName === "" ? "" : filter.customerName || "",
      line: filter.line || "",
      referenceNumber: filter.referenceNumber || "",
      licensePlate: filter.licensePlate || "",
      equipmentStatus: filter.equipmentStatus || "",
      equipmentStatusList: filter.equipmentStatusList || [],
      assignmentTypeList: filter.assignmentTypeList || [],
      commodityType: filter.commodityType || "",
      equipmentType: filter.equipmentType || "ALL",
      from: filter.from || null,
      to: filter.to || null,
      destination: filter.destination || "",
      origin: filter.origin || "",
      cargoType: filter.cargoType || "",
      commodity: filter.commodity || "",
      grade: filter.grade || "",
      lotNumber: filter.lotNumber || "",
      lastTracedLocation: filter.lastTracedLocation || "",
      servingYard: filter.servingYard || "",
      truckingCompany: filter.truckingCompany || "",
      equipmentSize: filter.equipmentSize || "",
      equipmentSizeList: filter.equipmentSizeList || [],
      shipper: filter.shipper || "",
      track: filter.track || "",
      vesselName: filter.vesselName || "",
      voyageNumber: filter.voyageNumber || "",
      filterTracingStatusList: filter.filterTracingStatusList || [],
      isOutgated: filter.isOutgated || false,
      ...(filter.tracingStatus !== 'TRANSIT' ? { [filter.tracingStatus]: filter.tracingDate } : {}),
      sortFields
    }
    
    requestBody = filterData
  } else {
    // Use single-sort endpoint
    uri = getUriParams(params)
    
    if (isSchedule) {
      endpoint = `${baseService}/GetScheduleEquipmentList?${uri}`
    } else {
      endpoint = `${baseService}/GetEquipmentByMovementType?${uri}`
    }
    
    requestBody = {
      movementType: filter.movementType || "",
      yardLocation: filter.yardLocation || "ALL",
      customerName: filter.customerName === "ALL" || filter.customerName === "" ? "" : filter.customerName || "",
      line: filter.line || "",
      referenceNumber: filter.referenceNumber || "",
      licensePlate: filter.licensePlate || "",
      equipmentStatus: filter.equipmentStatus || "",
      equipmentStatusList: filter.equipmentStatusList || [],
      assignmentTypeList: filter.assignmentTypeList || [],
      commodityType: filter.commodityType || "",
      equipmentType: filter.equipmentType || "ALL",
      from: filter.from || null,
      to: filter.to || null,
      destination: filter.destination || "",
      origin: filter.origin || "",
      cargoType: filter.cargoType || "",
      commodity: filter.commodity || "",
      grade: filter.grade || "",
      lotNumber: filter.lotNumber || "",
      lastTracedLocation: filter.lastTracedLocation || "",
      servingYard: filter.servingYard || "",
      truckingCompany: filter.truckingCompany || "",
      equipmentSize: filter.equipmentSize || "",
      equipmentSizeList: filter.equipmentSizeList || [],
      shipper: filter.shipper || "",
      track: filter.track || "",
      vesselName: filter.vesselName || "",
      voyageNumber: filter.voyageNumber || "",
      filterTracingStatusList: filter.filterTracingStatusList || [],
      isOutgated: filter.isOutgated || null,
      filterInvalidSchedule: filter.filterInvalidSchedule || null,
      filterNotTracing: filter.filterNotTracing || null,
      ...(filter.tracingStatus !== 'TRANSIT' ? { [filter.tracingStatus]: filter.tracingDate } : {})
    }
  }
  
  // Dispatched separately (not awaited) and in parallel with the list request, so stats loading
  // reflects the same window as the table load instead of only starting once the list finishes
  dispatch(getScheduleEquipmentStats(requestBody))

  const response = await axios.post(endpoint, requestBody)

  // Remove sortFields from filter to prevent React rendering issues
  const filterForState = { ...requestBody }
  if (filterForState.sortFields) {
    delete filterForState.sortFields
  }

  const searchQuery = data.params.q || ''
  const searchedEquipmentNumbers = searchQuery.split(/\s+/).filter(Boolean)

  return {
    params,
    searchedEquipmentNumbers,
    data: response.data.data,
    totalPages: response.data.totalCount,
    filter: filterForState
  }
})

export const getFFRequest = createAsyncThunk('ffRequests/getFFRequest', async id => {
  const response = await axios.get(`${urlFreightForwardService}/GetFFRequest/${id}`)
  const customer = await axios.get(`${urlIdentityService}/${response.data[0].customerUserId}`)
  return {
    selectedCustomer: customer.data.data[0],
    ffRequest: response.data[0],
    status: response.data[0].status,
    oceanBookings: response.data[0].oceanBookings,
    notes: response.data[0].notes
  }
})

export const getEquipment = async (equipmentId) => {
  try {
    const response = await axios.get(`${urlEquipmentService}/GetEquipment/${equipmentId}`)
    if (response.data.success) {
      return { success: true, data: response.data.data }
    } else {
      return { success: false, message: response.data.errors[0] }
    }
  } catch (error) {
    return { success: false, message: 'Error getting equipment' }
  }
}
// Function to get hold reasons from ConfigCache
export const getHoldReasons = async () => {
  try {
    const response = await axios.get(`${urlConfigCacheService}/GetAllSelect/HoldReason`)
      if (response.data.success) {
      return {
        success: true,
        data: response.data.data.map(item => ({
          value: item.value,
          label: item.label
        }))
      }
    } else {
      return { success: false, message: response.data.errors?.[0] || 'Failed to fetch hold reasons' }
    }
  } catch (error) {
    console.error('Error fetching hold reasons:', error)
    return { success: false, message: 'Error fetching hold reasons' }
  }
}

// Function to set hold inbound
export const setHoldInbound = async (data) => {
  try {
    // Transform the data to match backend structure
    const payload = {
      equipmentIds: data.equipmentIds,
      holdReasons: [data.holdReason], // Convert single reason to array
      holdDate: data.holdDate === null ? null : (data.holdDate instanceof Date ? data.holdDate.toISOString() : data.holdDate)
    }
    const response = await axios.post(`${urlEquipmentService}/SetHoldEquipment`, payload)
    if (response.data.success) {
      toast.success(response?.data?.messages || 'Hold inbound set successfully')
    } else {
      toast.error(response?.data?.errors?.[0] || 'Failed to set hold inbound')
    }
  } catch (error) {
    console.error('Error setting hold inbound:', error)

    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set hold inbound',
      data: null
    }
  }
}

///Equipment/UpdateEquipment
export const updateEquipment = async (equipment) => {
  //remove "addInfo" property from equipment "movement" array in object
  equipment.movements.forEach(m => {
    delete m.addInfo
  })
  try {
    const response = await axios.post(`${urlEquipmentService}/UpdateEquipment`, equipment)
    if (response.data.success) {
      //ToastMessage('Equipment Update', response.data.messages, true)
      return { success: true }
    } else {
      //ToastMessage('Equipment Update', response.data.messages, false)
      return {
        success: false,
        message: response.data.errors[0] || 'Error updating equipment'
      }
    }
  } catch (error) {
    console.log('error', error)
    //ToastMessage('Equipment Update', 'Error updating equipment', false)
    return { success: false }
  }
}

// Function to set damaged condition
export const setDamagedCondition = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/SetDamagedCondition`, data)
    if (response.data.success) {
      toast.success(response?.data?.messages || 'Damaged condition set successfully')
    } else {
      toast.error(response?.data?.errors?.[0] || 'Failed to set damaged condition')
    }
  } catch (error) {
    console.error('Error setting damaged condition:', error)
    toast.error('Error setting damaged condition')
  }
}

// Delete Movement
export const deleteMovement = async (equipmentId, movementId) => {
  try {
    const response = await axios.delete(`${urlMovementService}/DeleteMovement/${equipmentId}/${movementId}`)
    if (response.data.success) {
      return { success: true, message: response.data.messages[0] || 'Movement deleted successfully' }
    } else {
      return { success: false, message: response.data.errors[0] || 'Failed to delete movement' }
    }
  } catch (error) {
    console.error('Error deleting movement:', error)
    return { success: false, message: 'Error deleting movement' }
  }
}

// Validate Equipment
export const validateEquipment = async (equipmentId) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/SetEquipmentValid/${equipmentId}`)
    if (response.success) {
      return { success: true, data: response.data.data }
    } else {
      return { success: false, message: response.data.errors[0] || 'Failed to validate equipment' }
    }
  } catch (error) {
    console.error('Error validating equipment:', error)
    return { success: false, message: 'Error validating equipment' }
  }
}

//Equipment/SetCargo
export const setCargo = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/SetCargo`, data)
    if (response.data.success) {
      return { success: true, message: response.data.messages[0] }
    } else {
      return { success: false, message: response.data.errors }
    }
  } catch (error) {
    return { success: false, message: 'Error updating equipment' }
  }
}

export const UpdateEquipmentNotes = async (data) => {
  const response = await axios.post(`${urlEquipmentService}/UpdateEquipmentNotes`, data)
  if (response.data.success) {
    return { success: true, data: response.data.data }
  } else {
    return { success: false, message: response.data.errors[0] }
  }
}

export const setEquipmentDates = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/SetEquipmentDates`, data)
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        messages: response.data.messages || [],
        errors: response.data.errors || [],
        areAnyDemurrageToConfirm: response.data.areAnyDemurrageToConfirm || false
      }
    } else {
      return {
        success: false,
        message: response.data.errors?.[0] || response.data.messages?.[0] || 'Error updating equipment',
        messages: response.data.messages || [],
        errors: response.data.errors || [],
        areAnyDemurrageToConfirm: response.data.areAnyDemurrageToConfirm || false,
        data: response.data.data
      }
    }
  } catch (error) {
    return { success: false, message: 'Error updating equipment' }
  }
}

export const getAllRailcarsInDemurrage = async (page = 1, perPage = 10, equipmentNumber = '') => {
  try {
    const response = await axios.post(`${urlDemurrageService}/GetAllRailcars?perPage=${perPage}&page=${page}`, { equipmentNumber })
    if (response.data.success) {
      return { success: true, data: response.data.data, totalCount: response.data.totalCount }
    } else {
      return { success: false, message: response.data.errors?.[0] || 'Error fetching railcars in demurrage' }
    }
  } catch (error) {
    return { success: false, message: 'Error fetching railcars in demurrage' }
  }
}

// Fetch demurrage railcar data for a specific set of equipment (used by the
// Railcar Demurrage Report screen where the user selects rows to confirm).
export const getRailcarsInDemurrageByIds = async (equipmentIdList = []) => {
  try {
    const perPage = equipmentIdList.length || 10
    const response = await axios.post(`${urlDemurrageService}/GetAllRailcars?perPage=${perPage}&page=1`, { equipmentIdList })
    if (response.data.success) {
      return { success: true, data: response.data.data, totalCount: response.data.totalCount }
    } else {
      return { success: false, message: response.data.errors?.[0] || 'Error fetching railcars in demurrage' }
    }
  } catch (error) {
    return { success: false, message: 'Error fetching railcars in demurrage' }
  }
}

// Normalizes the server's `errors` payload into a flat list of strings so the UI can
// show every error, whether it arrives as an array, a single string, or an
// ASP.NET ModelState dictionary ({ field: ['message'] }).
const extractServerErrors = (payload) => {
  const errors = payload?.errors ?? payload?.Errors
  if (!errors) return []
  let list = []
  if (Array.isArray(errors)) {
    list = errors
  } else if (typeof errors === 'string') {
    list = [errors]
  } else if (typeof errors === 'object') {
    list = Object.values(errors).flat()
  }
  return list
    .filter(Boolean)
    .map(error => (typeof error === 'string' ? error : (error.message || error.description || String(error))))
}

export const confirmDemurrage = async (data) => {
  try {
    const response = await axios.post(`${urlDemurrageService}/Confirm`, data)
    if (response.data.success) {
      return { success: true, messages: response.data.messages || [], errors: [] }
    }
    // 200 with success = false: surface every error the server reported
    const errors = extractServerErrors(response.data)
    return { success: false, errors: errors.length ? errors : ['Error confirming demurrage'] }
  } catch (error) {
    const status = error?.response?.status
    if (!error?.response) {
      return { success: false, errors: ['Unable to reach the server. Please check your connection and try again.'] }
    }
    if (status >= 500) {
      return { success: false, errors: ['Internal server error. Please try again or contact support.'] }
    }
    // 4xx: the body still carries the validation/business errors
    const errors = extractServerErrors(error.response.data)
    return { success: false, errors: errors.length ? errors : ['Error confirming demurrage'] }
  }
}

export const getRailcarDemurrageReport = createAsyncThunk('equipment/getRailcarDemurrageReport', async (data) => {
  const { params, filters } = data
  const uri = getUriParams(params)
  const filterData = filters ? filters : {}
  const response = await axios.post(`${urlDemurrageService}/RailcarDemurrageReport?${uri}`, filterData)
  return {
    params,
    filterData: filters,
    data: response.data.data || [],
    totalPages: response.data.totalCount || 0
  }
})

export const getAllPositions = async (data) => {
  try {
    const response = await axios.post(`${urlMovementService}/GetAllPosition`, data)
    if (response.data.success) {
      return { success: true, data: response.data.data }
    } else {
      return { success: false, message: response.data.messages[0] }
    }
  } catch (error) {
    return { success: false, message: 'Error getting equipment status' }
  }
}

//Equipment/AssignPosition
export const assignPosition = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/AssignPosition`, data)
    return response.data
  } catch (error) {
    return { success: false, message: 'Error assigning position' }
  }
}

export const getTrackList = createAsyncThunk('movement/getTrackList', async (_, { rejectWithValue }) => {
  try {
    const KingswayYardTrack = await axios.get(`${urlConfigCacheService}?type=KingswayYardTrack`)
    const LindseyYardTrack = await axios.get(`${urlConfigCacheService}?type=LindseyYardTrack`)

    return {
      KingswayYardTrack: KingswayYardTrack.data,
      LindseyYardTrack: LindseyYardTrack.data
    }
  } catch (error) {
    return rejectWithValue('Error getting track list')
  }
})

export const assignTrack = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/AddUpdateTrackSequence`, data)
    return response.data
  } catch (error) {
    return { success: false, message: 'Error assigning truck' }
  }
}

export const setReferenceNumber = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/SetReferenceNumber`, data)
    return response.data
  } catch (error) {
    return { success: false, message: 'Error setting reference number' }
  }
}

//Equipment/AssignPosition
export const assignTrucker = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/AssignTruckingCompany`, data)
    return response.data
  } catch (error) {
    return { success: false, message: response.messages }
  }
}

//Equipment/AssignOceanBooking
export const assignOceanBooking = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/AssignOceanBooking`, data)
    return response.data
  } catch (error) {
    return { success: false, message: 'Error assigning ocean booking' }
  }
}

///DeleteEquipment/64c97d67215bc78157dffea9
export const deleteEquipment = async (equipmentId) => {
  try {
    const response = await axios.delete(`${urlEquipmentService}/DeleteEquipment/${equipmentId}`)
    console.log(response)
    if (response.data.success) {
      return { success: true }
    } else {
      return { success: false, message: response.data.messages[0] } // Only one message in array
    }
  } catch (error) {
    return { success: false, message: 'Internal Server Error' }
  }
}

export const getOceanBookingList = createAsyncThunk('ffRequests/getOceanBookingList', async oceanBookingIds => {
  const response = await axios.post(`${urlOceanBookingService}/getOceanBookingList`, oceanBookingIds)
  return {
    oceanBookingList: response.data.data.allData
  }
})

export const addIngate = createAsyncThunk('movement/addIngate', async (IngateViewModel, { dispatch, getState }) => {
  await axios.post(`${urlTerminalService}/AddMovement`, IngateViewModel)
  const state = getState().movement
  await dispatch(getData({
    params: state.params,
    filter: state.filter,
    sortFields: state.sortFields || []
  }))
  await dispatch(getAllData())
  return IngateViewModel
})

export const updateFFRequest = createAsyncThunk('ffRequests/updateFFRequest', async (FFRequestUpdateModel, { dispatch }) => {
  await axios.put(`${urlFreightForwardService}/UpdateFFRequest`, FFRequestUpdateModel)
  await dispatch(getFFRequest(FFRequestUpdateModel.id))
  return FFRequestUpdateModel
})

export const updateIngate = createAsyncThunk('movements/updateIngate', async (IngateUpdateModel, { dispatch }) => {
  await axios.put(`${urlTerminalService}/Update`, IngateUpdateModel)
  await dispatch(getFFRequest(IngateUpdateModel.id))
  return IngateUpdateModel
})

export const addNotes = createAsyncThunk('ffRequests/addNotes', async (FFNotesViewModel, { dispatch }) => {
  await axios.post(`${urlFreightForwardService}/AddFFNotes`, FFNotesViewModel)
  await dispatch(getFFRequest(FFNotesViewModel.FFRequestId))
  return FFNotesViewModel
})

export const updateStatus = createAsyncThunk('ffRequests/updateStatus', async (FFStatusUpdateModel, { dispatch }) => {
  await axios.post(`${urlFreightForwardService}/UpdateFFStatus`, FFStatusUpdateModel)
  await dispatch(getFFRequest(FFStatusUpdateModel.FFRequestId))
  return FFStatusUpdateModel
})

export const assignFFOceanBooking = createAsyncThunk('ffRequests/assignFFOceanBooking', async (FFOceanBookingAddModel, { dispatch }) => {
  const OceanBookingNumberModel = {
    OceanBookingNumber: FFOceanBookingAddModel.OceanBookingNumber
  }
  const OceanBookingId = await axios.post(`${urlOceanBookingService}/GetOceanBookingId`, OceanBookingNumberModel)
  const FFOceanBookingAssignModel = {
    FFRequestId: FFOceanBookingAddModel.FFRequestId,
    OceanBookingId: OceanBookingId.data.data.oceanBookigId,
    notes: FFOceanBookingAddModel.notes,
    noteType: FFOceanBookingAddModel.noteType
  }
  await axios.post(`${urlFreightForwardService}/AssignFFOceanBooking`, FFOceanBookingAssignModel)
  await dispatch(getFFRequest(FFOceanBookingAssignModel.FFRequestId))
  return FFOceanBookingAssignModel
})

// ================== EQUIPMENT ==================

export const getEquipmentInventoryStats = createAsyncThunk('equipment/getEquipmentInventoryStats', async filterData => {
  const statsResponse = await axios.post(`${urlEquipmentService}/GetEquipmentInventoryStats`, filterData)
  return { statsData: statsResponse.data.data }
})

export const getInventoryData = createAsyncThunk('equipment/getInventoryData', async (data, { dispatch }) => {
  const { params, filters } = data
  const uri = `sort=${params.sort || 'desc'}&sortColumn=${params.sortColumn || 'lastDateIn'}&page=${params.page || 1}&perPage=${params.perPage || 10}&q=${params.q || ''}`
  const filterData = filters ? filters : {}
  // Dispatched separately (not awaited) and in parallel with the list request, so stats loading
  // reflects the same window as the table load instead of only starting once the list finishes
  dispatch(getEquipmentInventoryStats(filterData))

  const response = await axios.post(`${urlEquipmentService}/GetEquipmentInventory?${uri}`, filterData)

  return {
    params,
    data: response.data.data,
    totalPages: response.data.totalCount,
    filterData: filters
  }
})

export const getStorageData = createAsyncThunk('equipment/getStorageData', async (data) => {
  const { params, filters } = data
  const uri = `sort=${params.sort || 'desc'}&sortColumn=${params.sortColumn || 'movementDate'}&page=${params.page || 1}&perPage=${params.perPage || 10}&q=${params.q || ''}`
  const filterData = filters ? filters : {}
  const response = await axios.post(`${urlEquipmentService}/GetEquipmentInventory?${uri}`, filterData)
  return {
    params,
    filterData: filters,
    data: response.data.data || [],
    totalPages: response.data.data.length > 0 ? response.data.totalCount : 1
  }
})

export const presendEDI = createAsyncThunk('equipment/presendEDI', async (data, { dispatch }) => {
  const { rows, params, filters } = data
  const response = await axios.post(`${urlEdiRecords}/AddMultipleEDIRecord`, rows)
  if (response.data.success) {
    dispatch(getInventoryData({ params, filters }))
  }
  return response.data
})
//submit selected equipment list for street turn
export const submitStreetTurn = createAsyncThunk(
  'equipment/submitStreetTurn',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${urlEquipmentService}/AddStreetTurnMovement`, data)
      if (response.data.success) {
        const { errors, messages } = response.data

        if (errors && errors.length > 0) {
          // Display errors in an ordered list
          ToastMessage('Error', errors.map((err, index) => `${index + 1}. ${err}`).join('\n'), false)
        }
        if (messages && messages.length > 0) {
          // Display success messages in an ordered list
          ToastMessage('Success', messages.map((msg, index) => `${index + 1}. ${msg}`).join('\n'), true)
        }
        return response.data

      } else {
        ToastMessage('Error', response.data.errors.map((err, index) => `${index + 1}. ${err}`).join('\n'), false)
        return rejectWithValue(response.data.errors)
      }
    } catch (error) {
      // Catch network or unexpected errors
      ToastMessage('Error', `Oops! Something went wrong: ${error.message}`, false)
      return rejectWithValue(error.message)
    }
  }
)

export const setCTCInboundEmpty = async (data) => {
  const res = await axios.post(`${urlEquipmentService}/SetCTCInboundEmpty`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

export const setCTCOutboundLoaded = async (data) => {
    const res = await axios.post(`${urlEquipmentService}/SetCTCOutboundLoaded`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

export const setCRSAInboundEmpty = async (data) => {
  const res = await axios.post(`${urlEquipmentService}/SetCRSAInboundEmpty`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

export const setCRSAOutboundLoaded = async (data) => {
  const res = await axios.post(`${urlEquipmentService}/SetCRSAOutboundLoaded`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

//get all yard locations
export const getAllYardLocations = createAsyncThunk('equipment/getAllYardLocations', async () => {
  const response = await axios.get(`${urlConfigCacheService}/YardLocation`)
  return response.data
})

// export const getDrivers = async (params) => {

//   const uri =  `${getUserUriParams(params)}${params?.company ? `&company=${params.company}` : ''}`
//   const response = await axios.get(`${urlIdentityService}/GetAllUsers?${uri}`)
//   if (response.data.success) {
//     return {success: true, data: response.data.data}
//   } else {
//     return {success: false, message: response.data.errors[0], data: []}
//   }
// }

// Updated Driver Endpoint
export const getDrivers = async (data) => {
  console.log('ENDPOINT', data)
  const { params, filters } = data
  console.log(params, filters)
  const uri = `sort=${params.sort || 'desc'}&page=${params.page || 1}&perPage=${params.perPage || 500}&q=${params.q || ''}`
  console.log(uri)
  const response = await axios.post(`${urlDriverService}/GetTruckDrivers?${uri}`, filters)
  console.log(uri)
  if (response.data.success) {
    return {
      success: true,
      data: response.data.data
    }
  } else {
    return {
      success: false,
      message: response.data.messages[0]
    }
  }
}

export const findMultipleEqOnSchedule = async (data) => {
  const res = await axios.post(`${urlEquipmentService}/FindMultipleEquipmentOnSchedule`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

export const FindEquipmentOnSystem = async (data) => {
  const res = await axios.post(`${urlEquipmentService}/FindEquipmentOnSystem`, data)
  if (res.data.totalCount > 0) {
    return { success: true, data: res.data.data }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

// Upload Excel Files
export const uploadCTCExcelFile = async (data) => {
  const res = await axios.post(`${urlMovementService}/UploadCTCExcelFile`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data, messages: res.data.messages }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

export const uploadCTCExcelETA = async (data) => {
  const res = await axios.post(`${urlMovementService}/UploadCTCExcelETA`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data, messages: res.data.messages, errors: res.data.errors } // Show all errors
  } else {
    return { success: false, message: res.data.errors }
  }
}

export const uploadCRSAExcelFile = async (data) => {
  const res = await axios.post(`${urlMovementService}/UploadCRSAExcelFile`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data, messages: res.data.messages }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

export const uploadTrackSequenceFile = async (data) => {
  const res = await axios.post(`${urlEquipmentService}/UploadTrackSequence`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data, messages: res.data.messages }
  } else {
    return { success: false, message: res.data.errors }
  }
}

export const uploadBlockedEquipmentFile = async (data) => {
  const res = await axios.post(`${urlMovementService}/UploadBlockedEquipmentFile`, data)
  if (res.data.success) {
    return { success: true, data: res.data.data, messages: res.data.messages }
  } else {
    return { success: false, message: res.data.errors[0] }
  }
}

//Get all dispatch data
export const GetAllDispatch = async (params, filters) => {
  const uri = `page=${params?.page || 1}&perPage=${params?.perPage || 1000}&status=${params?.status || ""}&q=${params?.q || ""}&sort=${params?.sort || "asc"}&sortColumn=${params?.sortColumn || "id"}`
  if (filters && filters.fromDate === "") {
    delete filters.fromDate
    delete filters.toDate
  }
  try {
    const response = await axios.post(`${urlDispatchService}/GetDispatch?${uri}`, filters || {})
    // const response = await axios.post(`${urlDispatchService}/GetDispatch`, {})
    if (response.status === 200) {
      return { data: response.data, success: true }
    } else {
      return { success: false, message: 'Internal server Error' }
    }
  } catch (error) {
    return { success: false, message: 'Internal server Error' }
  }
}

//Retrieve all block equipment
export const getBlockedEquipment = createAsyncThunk('equipment/getBlockedEquipment', async (data) => {
  const { params, filters } = data
  const uri = `sort=${params.sort || 'desc'}&sortColumn=${params.sortColumn || 'lastDateIn'}&page=${params.page || 1}&perPage=${params.perPage || 10}&q=${params.q || ''}`
  const filterData = filters ? filters : {}
  const response = await axios.post(`${urlEquipmentService}/GetBlockedEquipment?${uri}`, filterData)
  return {
    params,
    filterData: filters,
    data: response.data.data || [],
    totalPages: response.data.totalCount
  }
})

export const setBlockEquipment = async (data) => {
  const response = await axios.post(`${urlEquipmentService}/BlockEquipment`, data)
  if (response.data.success) {
    return { success: true, data: response.data.data }
  } else {
    return { success: false, message: response.data.messages }
  }
}

//Get Container Codes from ConfigCache
export const getContainerSize = createAsyncThunk('configCache/containerSize', async () => {
  const response = await axios.get(`${urlConfigCacheService}/ContainerSize`)
  return response.data

})

// /ConfigCache/ContainerSize
export const getContainerSizeData = createAsyncThunk(
  "companyConfig/getContainerSizeData",
  async () => {
    const response = await axios.get(`${urlConfigCacheService}?type=ContainerSize`)
    return response.data ? response.data.sort((a, b) => a.id.localeCompare(b.id)) : []
  }
)
//set intact API
export const setIntact = createAsyncThunk('equipment/setIntact', async (data) => {
  try {
    // Helper function to format messages as an ordered list
    const formatMessages = (messagesArray) => {
      if (Array.isArray(messagesArray) && messagesArray.length > 0) {
        return (
          <ol>
            {messagesArray.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ol>
        )
      }
      return messagesArray// Return the original if it's not an array or empty
    }
    const response = await axios.post(`${urlEquipmentService}/SetIntact`, data)
    // Check for success and display messages or errors in ordered list format
    if (response.data.success) {
      const formattedMessages = formatMessages(response.data.messages)
      ToastMessage('Success', formattedMessages, true)
    } else {
      const formattedErrors = formatMessages(response.data.errors)
      ToastMessage('Error', formattedErrors, false)
    }
    return response.data
  } catch (error) {
    console.error("Error in setIntact API call:", error)
    throw error
  }
})

// Movement - Get RefNumbers from location
export const getRefNumbers = createAsyncThunk('movement/getMovementReferences', async data => {
  const { params, filter } = data
  const uri = getUriParams(params)
  const filterData = {
    type: filter.type || "",
    referenceNumber: filter.referenceNumber || "",
    equipmentSize: filter.equipmentSize || "",
    ssLineCode: filter.ssLineCode || ""
  }
  const response = await axios.post(`${urlMovementService}/GetMovementReferences?${uri}`, filterData)
  return {
    params,
    data: response.data.data,
    totalPages: response.data.totalCount
  }
})

export const turnaroundEquipmentFromAdmin = async (data) => {
  try {
    const response = await axios.post(`${urlTurnaroundService}/TurnaroundEquipmentFromAdmin`, data)
    if (response.data.success) {
      return { success: true, message: response.data.messages?.[0] || 'Turnaround created successfully' }
    } else {
      return { success: false, message: response.data.errors?.[0] || 'Failed to create turnaround' }
    }
  } catch (error) {
    return { success: false, message: 'Internal server Error' }
  }
}

// Request Street Turn for selected equipment
export const requestStreetTurn = async (data) => {
  try {
    const response = await axios.post(`${urlEquipmentService}/RequestStreetTurn`, data)
    if (response.data.success) {
      return { success: true, message: response.data.messages?.[0] || 'Street turn requested successfully' }
    } else {
      return { success: false, message: response.data.errors?.[0] || 'Failed to request street turn' }
    }
  } catch (error) {
    return { success: false, message: 'Internal server Error' }
  }
}

// Get All Depot
export const getAllDepot = async () => {
  try {
    const response = await axios.get(`${urlEquipmentService}/GetAllDepot`)
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data
      }
    } else {
      return {
        success: false,
        message: response.data.messages[0] || 'Error Getting Depot'
      }
    }
  } catch (error) {
    return {
      success: false,
      message: 'Internal Server Error'
    }
  }
}

// Get live booking container counts (Requested/Loaded/Outbound) for the Schedule List Excel export (AB#12417)
export const getOceanBookingContainerDetails = async equipments => {
  try {
    const response = await axios.post(`${urlOceanBookingService}/GetAllOceanBookingContainerDetails`, equipments)
    return {
      success: true,
      data: response.data || []
    }
  } catch (error) {
    return {
      success: false,
      message: 'Internal Server Error'
    }
  }
}

export const ffRequestsSlice = createSlice({
  name: 'ffRequests',
  initialState: {
    data: [],
    total: 1,
    params: {
      sort: "desc",
      sortColumn: "MovementDate",
      q: '',
      page: 1,
      perPage: 10,
      status: ""
    },
    filter: {
      movementType: "ingate",
      yardLocation: "kingsway",
      customerName: "",
      line: "",
      referenceNumber: "",
      licensePlate: "",
      equipmentStatus: "",
      assignmentType: "",
      equipmentType: "",
      from: null,
      to: null,
      filterTracingStatusList: []
    },
    allData: {},
    selectedCustomer: null,
    ffRequest: null,
    status: [],
    yardLocations: [],
    containerSize: [],
    oceanBookings: [],
    oceanBookingList: [],
    notes: [],
    sortFields: [],
    loading: false,
    statsLoading: false,
    refNumbers: [],
    trackList: {
      data: {
        KingswayYardTrack: [],
        LindseyYardTrack: []
      },
      loading: false,
      error: null
    },
    equipment: {
      inventory: {
        data: [],
        loading: false,
        statsLoading: false,
        total: 1,
        params: {},
        filter: {},
        stats: null,
        notes: [],
        errors: [],
        successMessages: []
      },
      storage: {
        data: [],
        loading: false,
        total: 1,
        params: {},
        filter: {},
        notes: []
      },
      railcarDemurrageReport: {
        data: [],
        loading: false,
        total: 1,
        params: {
          sort: 'desc',
          sortColumn: 'AvailableDate',
          page: 1,
          perPage: 10,
          q: ''
        },
        filter: {}
      }
    }
  },
  
  reducers: {
    clearErrors: (state) => {
      state.equipment.inventory.errors = []
      // state.equipment.storage.errors = []
    },
    clearSuccessMessages: (state) => {
      state.equipment.inventory.successMessages = []
      // state.equipment.storage.successMessages = []
    },
    // Register a column for sorting if not already present
    registerSortColumn: (state, action) => {
      const { columnKey } = action.payload
      // Initialize sortFields if it doesn't exist
      if (!state.sortFields) {
        state.sortFields = []
      }
      // Check if column is already registered (optional, for backwards compatibility)
      if (!state.sortFields.some(s => s.column === columnKey)) {
      }
    },
    // Update sort order/direction for a column
    updateSortOrder: (state, action) => {
      const { columnKey, column, direction } = action.payload
      
      // Initialize sortFields if it doesn't exist
      if (!state.sortFields) {
        state.sortFields = []
      }
      
      const columnName = column || columnKey
      
      // Remove existing entry for this column
      state.sortFields = state.sortFields.filter(s => s.column !== columnName)
      
      // Add to sortFields if there's a valid direction
      if (direction && direction !== 'none') {
        state.sortFields.push({
          column: columnName,
          direction
        })
      }
    },
    deregisterSortColumn: (state, action) => {
      const { columnKey } = action.payload
      // Initialize sortFields if it doesn't exist
      if (!state.sortFields) {
        state.sortFields = []
      }
      // Remove from sortFields
      state.sortFields = state.sortFields.filter(s => s.column !== columnKey)
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAllData.fulfilled, (state, action) => {
        state.allData = action.payload
      })
      .addCase(getData.fulfilled, (state, action) => {
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.totalPages
        state.filter = action.payload.filter // Used for Schedule Excel
        state.loading = false
        state.searchedEquipmentNumbers = action.payload.searchedEquipmentNumbers
      })
      .addCase(getScheduleEquipmentStats.pending, state => {
        state.statsLoading = true
      })
      .addCase(getScheduleEquipmentStats.fulfilled, (state, action) => {
        state.statsLoading = false
        state.equipment.stats = action.payload.statsData
      })
      .addCase(getScheduleEquipmentStats.rejected, (state, action) => {
        state.statsLoading = false
        console.error('Failed to fetch schedule equipment stats', action.error)
      })
      .addCase(getInventoryData.fulfilled, (state, action) => {
        state.equipment.inventory.loading = false
        state.equipment.inventory.data = action.payload.data || []
        state.equipment.inventory.params = action.payload.params
        state.equipment.inventory.filter = action.payload.filterData
        state.equipment.inventory.total = action.payload.totalPages
      })
      .addCase(getEquipmentInventoryStats.pending, state => {
        state.equipment.inventory.statsLoading = true
      })
      .addCase(getEquipmentInventoryStats.fulfilled, (state, action) => {
        state.equipment.inventory.statsLoading = false
        state.equipment.inventory.stats = action.payload.statsData
      })
      .addCase(getEquipmentInventoryStats.rejected, (state, action) => {
        state.equipment.inventory.statsLoading = false
        console.error('Failed to fetch equipment inventory stats', action.error)
      })
      .addCase(getBlockedEquipment.fulfilled, (state, action) => {
        state.equipment.inventory.data = action.payload.data || []
        state.equipment.inventory.params = action.payload.params
        state.equipment.inventory.filter = action.payload.filterData
        state.equipment.inventory.total = action.payload.totalPages
        state.equipment.inventory.loading = false
      })
      .addCase(presendEDI.fulfilled, (state, action) => {
        state.equipment.inventory.errors = action.payload.errors || []
        state.equipment.inventory.successMessages = action.payload.messages || []
        state.equipment.inventory.loading = false
      })
      .addCase(presendEDI.rejected, (state) => {
        state.equipment.inventory.loading = false
      })
      .addCase(getStorageData.fulfilled, (state, action) => {
        state.equipment.storage.data = action.payload.data || []
        state.equipment.storage.params = action.payload.params
        state.equipment.storage.filter = action.payload.filterData
        state.equipment.storage.total = action.payload.totalPages
        state.equipment.storage.loading = false
      })
      .addCase(getFFRequest.fulfilled, (state, action) => {
        state.selectedCustomer = action.payload.selectedCustomer
        state.ffRequest = action.payload.ffRequest
        state.status = action.payload.status
        state.oceanBookings = action.payload.oceanBookings
        state.notes = action.payload.notes
      })
      .addCase(getOceanBookingList.fulfilled, (state, action) => {
        state.oceanBookingList = action.payload.oceanBookingList
      })
      .addCase(getData.pending, state => {
        state.loading = true
        state.data = []
      })
      .addCase(getData.rejected, (state) => {
        state.loading = false
        state.data = []
      })
      .addCase(getInventoryData.pending, state => {
        state.equipment.inventory.loading = true
        state.equipment.inventory.data = []
      })
      .addCase(getInventoryData.rejected, (state) => {
        state.equipment.inventory.loading = false
        state.equipment.inventory.data = []
      })
      .addCase(getBlockedEquipment.pending, state => {
        state.equipment.inventory.loading = true
        state.equipment.inventory.data = []
      })
      .addCase(getBlockedEquipment.rejected, (state) => {
        state.equipment.inventory.loading = false
        state.equipment.inventory.data = []
      })
      .addCase(getStorageData.pending, state => {
        state.equipment.storage.loading = true
        state.equipment.storage.data = []
      })
      .addCase(getStorageData.rejected, (state) => {
        state.equipment.storage.loading = false
        state.equipment.storage.data = []
      })
      .addCase(getRailcarDemurrageReport.fulfilled, (state, action) => {
        state.equipment.railcarDemurrageReport.data = action.payload.data || []
        state.equipment.railcarDemurrageReport.params = action.payload.params
        state.equipment.railcarDemurrageReport.filter = action.payload.filterData
        state.equipment.railcarDemurrageReport.total = action.payload.totalPages
        state.equipment.railcarDemurrageReport.loading = false
      })
      .addCase(getRailcarDemurrageReport.pending, state => {
        state.equipment.railcarDemurrageReport.loading = true
        state.equipment.railcarDemurrageReport.data = []
      })
      .addCase(getRailcarDemurrageReport.rejected, (state) => {
        state.equipment.railcarDemurrageReport.loading = false
        state.equipment.railcarDemurrageReport.data = []
      })
      .addCase(getAllYardLocations.fulfilled, (state, action) => {
        state.yardLocations = action.payload
      })
      .addCase(getContainerSize.fulfilled, (state, action) => {
        state.containerSize = action.payload
      })
      .addCase(getContainerSizeData.fulfilled, (state, action) => {
        state.containerSizes = action.payload
      })
      .addCase(getTrackList.pending, (state) => {
        state.trackList.loading = true
        state.trackList.error = null
      })
      .addCase(getTrackList.fulfilled, (state, action) => {
        state.trackList.loading = false
        state.trackList.data = action.payload
      })
      .addCase(getTrackList.rejected, (state, action) => {
        state.trackList.loading = false
        state.trackList.error = action.payload || 'Error getting track list'
      })
      .addCase(getRefNumbers.fulfilled, (state, action) => {
        state.refNumbers = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.totalPages
      })
      
  }
})

export const { clearErrors, clearSuccessMessages, registerSortColumn, updateSortOrder, deregisterSortColumn } = ffRequestsSlice.actions

export default ffRequestsSlice.reducer
